import { chain, cloneDeep, intersection, uniq } from 'lodash';

export const isCompound = node => {
  return node.type === 'compound';
};

export const isReaction = node => {
  return node.type === 'reaction';
};

const belongsToBestSubtree = node => {
  return Boolean(node.best);
};

const markRedundantSuccessors = (node, index, nodes) => {
  const result = {
    ...node,
    unexpandedSuccessors: [...node.successors],
    substratesToMap: node.substratesToMap
      ? node.substratesToMap.filter((s, i) => nodes[(node.successors || [])[i]])
      : [],
    successors: node.successors.filter(succ => nodes[succ]),
  };

  if (isCompound(node)) {
    return {
      ...result,
      reactionIds: node.successors,
    };
  }

  return result;
};

export const getNodesPathToUnexpandedNode = (nodes, tree, currentReactionPath, targetNodeId) => {
  const treePath = [];

  let node = nodes[targetNodeId];
  while (node) {
    if (node.smiles) {
      const selectedReactions =
        node.unexpandedSuccessors && intersection(node.unexpandedSuccessors, currentReactionPath.flat());
      if (selectedReactions.length > 0) {
        treePath.push([selectedReactions[0]]);
      } else {
        treePath.push([node.successors ? node.successors[0] || -1 : node.unexpandedSuccessors[0] || -1]);
      }
    }

    node = nodes[node.predecessor] || tree[node.predecessor];
  }

  treePath.reverse();
  return treePath;
};

export const mergeTreePaths = (treePath, reactionPath) => {
  const merged = cloneDeep(treePath);

  reactionPath.forEach((part, i) => {
    if (merged[i]) {
      if (Array.isArray(part)) {
        merged[i] = [...merged[i], ...part].flat();
      } else {
        merged[i].push(part);
      }
    } else {
      merged[i] = [part].flat();
    }
  });

  return merged.map(part => uniq(part));
};

export const getParentMoleculeNodeId = (nodes, node) => {
  if (!node.predecessor || !nodes[node.predecessor]) {
    return null;
  }

  return nodes[node.predecessor].predecessor;
};

export const getChildrenCompounds = data => node => {
  return {
    ...node,
    children: node.successors.reduce((acc, curr) => {
      return [...acc, ...data[curr].successors];
    }, []),
  };
};

export const findMissingReactionIds = (tree, reactionPath) =>
  chain(reactionPath)
    // .filter(reactionId => isReaction(tree[reactionId]))
    .difference(chain(tree).pickBy(belongsToBestSubtree).pickBy(isReaction).keys().value())
    .value();

export const findSubtreeIdsFrom = (tree, stopCompound, items = []) => {
  const node = tree[stopCompound];
  if (!node) return [];
  items.push(node.id);
  if (node.successors && node.successors.length > 0) {
    node.successors.forEach(item => findSubtreeIdsFrom(tree, item, items));
  }
  return items;
};

export const createReactionLinkData = (tree, reactionPath) => {
  return chain(tree)
    .filter(isCompound)
    .reduce((acc, node) => {
      const { id, successors } = node;

      if (successors.length === 0) {
        return {
          ...acc,
          [id]: {
            currentReactionId: null,
            prediction: null,
            reactionIds: [null, null, null, null],
          },
        };
      }

      let currentIdx = successors.findIndex(s => reactionPath.includes(s));
      currentIdx = currentIdx > -1 ? currentIdx : 0;

      const bestIdx = currentIdx > 0 ? 0 : null;
      const betterIdx = currentIdx > 0 ? currentIdx - 1 : null;
      const worseIdx = currentIdx < successors.length - 1 ? currentIdx + 1 : null;
      const worstIdx = currentIdx < successors.length - 1 ? successors.length - 1 : null;

      const currentReactionId = successors[currentIdx];
      let prediction = null;
      if (currentReactionId) {
        const reaction = tree[currentReactionId];
        if (reaction) {
          prediction = reaction.prediction;
        }
      }
      return {
        ...acc,
        [id]: {
          currentReactionId,
          prediction,
          reactionIds: [
            successors[bestIdx] || null,
            successors[betterIdx] || null,
            successors[worseIdx] || null,
            successors[worstIdx] || null,
          ],
        },
      };
    }, {})
    .value();
};

export const hasDifferentReference = (obj, obj2) => obj !== obj2;
